import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Collapse,
} from '@mui/material';
import DateRangeSelector from '../components/DateRangeSelector';
import {
  BarChart as BarChartIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  UnfoldMore as ExpandAllIcon,
  UnfoldLess as CollapseAllIcon,
  ArrowUpward as MoveUpIcon,
  ArrowDownward as MoveDownIcon,
  FileDownload as FileDownloadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import api from '../services/api';

// Level key → display name (organization not in "available" list; org is from context)
const LEVEL_OPTIONS: { key: string; label: string; isDateExpansion?: boolean }[] = [
  { key: 'name', label: 'User' },
  { key: 'company', label: 'Company' },
  { key: 'account', label: 'Account' },
  { key: 'task', label: 'Task' },
  { key: 'date', label: 'Date', isDateExpansion: true }, // Expands to year, month, day in API
  { key: 'week', label: 'Week' },
  { key: 'description', label: 'Description' },
];

const LEVEL_KEY_TO_LABEL: Record<string, string> = {
  name: 'User',
  organization: 'Organization',
  company: 'Company',
  account: 'Account',
  task: 'Task',
  year: 'Year',
  month: 'Month',
  week: 'Week',
  day: 'Day',
  description: 'Description',
};

export interface PivotNode {
  id: string;
  label: string;
  level: string;
  hours: number;
  children?: PivotNode[];
}

export interface PivotReportMetadata {
  totalHours: number;
  totalEntries: number;
  levels: string[];
  dateRange: { from: string; to: string };
  generatedAt: string;
}

interface ReportProps {
  selectedOrganizationId?: string;
}

function getDefaultDateRange(): { dateFrom: string; dateTo: string } {
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - 30);
  return {
    dateFrom: from.toISOString().slice(0, 10),
    dateTo: to.toISOString().slice(0, 10),
  };
}

function normalizeDateRange(dateFrom: string, dateTo: string): { dateFrom: string; dateTo: string } {
  const d1 = new Date(dateFrom).getTime();
  const d2 = new Date(dateTo).getTime();
  if (d1 <= d2) return { dateFrom, dateTo };
  return { dateFrom: dateTo, dateTo: dateFrom };
}

// When user adds "Date" in UI, we send year, month, day in that order
function reportFieldsToLevels(reportFields: string[]): string[] {
  const levels: string[] = [];
  for (const key of reportFields) {
    if (key === 'date') {
      levels.push('year', 'month', 'day');
    } else {
      levels.push(key);
    }
  }
  return levels;
}

// Build "available" keys: all options minus those already in report fields (and collapse date back to one)
function getAvailableKeys(reportFields: string[]): string[] {
  const used = new Set<string>();
  for (const key of reportFields) {
    if (key === 'date') used.add('date');
    else used.add(key);
  }
  return LEVEL_OPTIONS.filter((o) => !used.has(o.key)).map((o) => o.key);
}

function getAllExpandableNodeIds(nodes: PivotNode[]): Set<string> {
  const ids = new Set<string>();
  for (const node of nodes) {
    if (node.children && node.children.length > 0) {
      ids.add(node.id);
      getAllExpandableNodeIds(node.children).forEach((id) => ids.add(id));
    }
  }
  return ids;
}

function flattenTreeForCsv(nodes: PivotNode[], path: string[]): { level: string; label: string; hours: number; path: string }[] {
  const rows: { level: string; label: string; hours: number; path: string }[] = [];
  for (const node of nodes) {
    const currentPath = [...path, node.label];
    const pathStr = currentPath.join(' > ');
    rows.push({ level: node.level, label: node.label, hours: node.hours, path: pathStr });
    if (node.children && node.children.length > 0) {
      rows.push(...flattenTreeForCsv(node.children, currentPath));
    }
  }
  return rows;
}

function escapeCsvValue(val: string): string {
  if (/[",\n\r]/.test(val)) {
    return `"${val.replace(/"/g, '""')}"`;
  }
  return val;
}

export default function Report({ selectedOrganizationId }: ReportProps) {
  const { organizationId: urlOrganizationId } = useParams<{ organizationId: string }>();
  const organizationId = selectedOrganizationId || urlOrganizationId;

  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [reportFields, setReportFields] = useState<string[]>([]);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [filterLevel, setFilterLevel] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<PivotNode[] | null>(null);
  const [metadata, setMetadata] = useState<PivotReportMetadata | null>(null);
  const [expandedNodeIds, setExpandedNodeIds] = useState<Set<string>>(new Set());

  const availableKeys = useMemo(() => getAvailableKeys(reportFields), [reportFields]);
  const canGenerate = Boolean(organizationId && reportFields.length > 0);
  const canExport = Boolean(data && data.length > 0);

  const addLevel = (key: string) => {
    setReportFields((prev) => [...prev, key]);
    setError(null);
  };

  const removeLevel = (index: number) => {
    setReportFields((prev) => prev.filter((_, i) => i !== index));
    setError(null);
  };

  const moveLevel = (index: number, direction: 'up' | 'down') => {
    setReportFields((prev) => {
      const next = [...prev];
      const j = direction === 'up' ? index - 1 : index + 1;
      if (j < 0 || j >= next.length) return prev;
      [next[index], next[j]] = [next[j], next[index]];
      return next;
    });
  };

  const buildFiltersForApi = (): Record<string, string[]> | undefined => {
    const entries = Object.entries(filters).filter(([, v]) => v.trim() !== '');
    if (entries.length === 0) return undefined;
    const out: Record<string, string[]> = {};
    for (const [k, v] of entries) {
      const values = v.split(',').map((s) => s.trim()).filter(Boolean);
      if (values.length > 0) out[k] = values;
    }
    return Object.keys(out).length > 0 ? out : undefined;
  };

  const handleGenerate = async () => {
    if (!organizationId || reportFields.length === 0) return;
    setLoading(true);
    setError(null);
    const { dateFrom, dateTo } = normalizeDateRange(dateRange.dateFrom, dateRange.dateTo);
    const levels = reportFieldsToLevels(reportFields);
    const apiFilters = buildFiltersForApi();
    try {
      const response = await api.getPivotTableReport({
        organizationId,
        dateFrom,
        dateTo,
        levels,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...(apiFilters && { filters: apiFilters }),
      });
      if (response.success && response.data) {
        setData(response.data);
        setMetadata(response.metadata || null);
        setExpandedNodeIds(new Set());
      } else {
        setError(response.message || 'Failed to generate report');
        setData(null);
        setMetadata(null);
      }
    } catch (err: any) {
      const message = err.response?.data?.message || err.response?.status === 404
        ? 'The reports API is not available yet. This feature is coming soon!'
        : err.message || 'Failed to generate report';
      setError(message);
      setData(null);
      setMetadata(null);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCsv = () => {
    if (!data || data.length === 0) return;
    const rows = flattenTreeForCsv(data, []);
    const header = 'Level,Label,Hours,Path';
    const lines = [header, ...rows.map((r) => [r.level, r.label, r.hours.toFixed(2), r.path].map(escapeCsvValue).join(','))];
    const csv = lines.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const now = new Date();
    const stamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}-${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
    a.download = `timesentinel-report-${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleExpand = (id: string) => {
    setExpandedNodeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => {
    if (data && data.length > 0) setExpandedNodeIds(getAllExpandableNodeIds(data));
  };

  const collapseAll = () => {
    setExpandedNodeIds(new Set());
  };

  const renderTree = (nodes: PivotNode[], depth: number) => {
    return nodes.map((node) => {
      const hasChildren = node.children && node.children.length > 0;
      const isExpanded = expandedNodeIds.has(node.id);
      return (
        <Box key={node.id} sx={{ pl: depth * 2.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              py: 0.25,
              borderBottom: '1px dotted',
              borderColor: 'divider',
              minHeight: 32,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
              {hasChildren ? (
                <IconButton size="small" onClick={() => toggleExpand(node.id)} sx={{ mr: 0.5, p: 0.25 }}>
                  {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              ) : (
                <Box sx={{ width: 28 }} />
              )}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: depth === 0 ? 600 : 400,
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {node.label}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500, ml: 1 }}>
              {typeof node.hours === 'number' ? node.hours.toFixed(2) : '0.00'}h
            </Typography>
          </Box>
          {hasChildren && (
            <Collapse in={isExpanded} unmountOnExit>
              {renderTree(node.children!, depth + 1)}
            </Collapse>
          )}
        </Box>
      );
    });
  };

  if (!organizationId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Select an organization to view reports.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
        Report
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Generate pivot table reports from your timesheet data.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Organization
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Current organization (ID: {organizationId})
          </Typography>

          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Date range
          </Typography>
          <Box sx={{ mb: 2 }}>
            <DateRangeSelector value={dateRange} onChange={setDateRange} size="small" />
          </Box>

          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Available fields
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {LEVEL_OPTIONS.filter((o) => availableKeys.includes(o.key)).map((o) => (
              <Chip
                key={o.key}
                label={o.label}
                onClick={() => addLevel(o.key)}
                size="small"
                icon={<AddIcon />}
                variant="outlined"
              />
            ))}
            {availableKeys.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                All fields are in use. Remove one from Report fields to add it here.
              </Typography>
            )}
          </Box>

          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Report fields (order = hierarchy)
          </Typography>
          <List dense sx={{ bgcolor: 'grey.100', borderRadius: 1, mb: 2, py: 0 }}>
            {reportFields.map((key, index) => (
              <ListItem
                key={`${key}-${index}`}
                secondaryAction={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IconButton size="small" onClick={() => moveLevel(index, 'up')} disabled={index === 0}>
                      <MoveUpIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => moveLevel(index, 'down')} disabled={index === reportFields.length - 1}>
                      <MoveDownIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => removeLevel(index)}>
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                  </Box>
                }
              >
                <ListItemText primary={key === 'date' ? 'Date' : LEVEL_KEY_TO_LABEL[key] || key} />
              </ListItem>
            ))}
            {reportFields.length === 0 && (
              <ListItem>
                <ListItemText primary="No levels selected. Click an available field above to add it." primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
              </ListItem>
            )}
          </List>

          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Filters (optional)
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Filter by level</InputLabel>
              <Select
                value={filterLevel}
                label="Filter by level"
                onChange={(e) => setFilterLevel(e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {reportFields.filter((k) => k !== 'date').map((k) => (
                  <MenuItem key={k} value={k}>{LEVEL_KEY_TO_LABEL[k] || k}</MenuItem>
                ))}
                {reportFields.includes('date') && (
                  <>
                    <MenuItem value="year">Year</MenuItem>
                    <MenuItem value="month">Month</MenuItem>
                    <MenuItem value="day">Day</MenuItem>
                  </>
                )}
              </Select>
            </FormControl>
            {filterLevel && (
              <TextField
                size="small"
                placeholder="Comma-separated values"
                value={filters[filterLevel] ?? ''}
                onChange={(e) => setFilters((prev) => ({ ...prev, [filterLevel]: e.target.value }))}
                sx={{ minWidth: 240 }}
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <BarChartIcon />}
              onClick={handleGenerate}
              disabled={!canGenerate || loading}
            >
              {loading ? 'Generating…' : 'Generate Report'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportCsv}
              disabled={!canExport}
            >
              Export CSV
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
              <CircularProgress size={24} />
              <Typography color="text.secondary">Generating report…</Typography>
            </Box>
          )}
          {!loading && error && (
            <Box sx={{ py: 2 }}>
              <Alert severity="error" action={
                <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={handleGenerate}>
                  Try Again
                </Button>
              }>
                {error}
              </Alert>
            </Box>
          )}
          {!loading && !error && !data?.length && (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              No report data. Select at least one hierarchy level and click Generate Report.
            </Typography>
          )}
          {!loading && !error && data && data.length > 0 && (
            <>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
                {metadata && (
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
                    Total: {metadata.totalHours.toFixed(2)}h · {metadata.totalEntries} entries · {metadata.dateRange?.from} to {metadata.dateRange?.to}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" variant="outlined" startIcon={<ExpandAllIcon />} onClick={expandAll}>
                    Expand all
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<CollapseAllIcon />} onClick={collapseAll}>
                    Collapse all
                  </Button>
                </Box>
              </Box>
              <Box sx={{ maxHeight: 480, overflow: 'auto' }}>
                {renderTree(data, 0)}
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
